import { faChevronLeft, faChevronRight, faTag, faTrophy } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { KeywordPagingGq } from '../../../../graphql/projects/models/KeywordPagingGq';
import { ProjectKeywordGq } from '../../../../graphql/projects/models/ProjectKeywordGq';
import { GetSelectedKeywordsStatsGq } from '../../../../graphql/projects/queries/useGetSelectedKeywordsStatsGq';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import { formatPercent } from '../../../../logic/otherUtilities/formatting/formatPercent';
import VoxelIconButton from '../../../voxel/Buttons/VoxelIconButton';
import VoxelSelect from '../../../voxel/Forms/VoxelSelect';
import VoxelText from '../../../voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../voxel/Tooltip/VoxelTooltipShort';
import { TableUIState } from './logic/TableUIState';
import { KeywordTablePage } from './KeywordTable';

export interface KeywordTablePaginationProps {
  tableRef?: HTMLDivElement;
  currentPage?: number;
  totalCount?: number;
  pagingState?: KeywordPagingGq;
  setPagingState?: React.Dispatch<React.SetStateAction<KeywordPagingGq>>;
  selectedKeywordStats?: GetSelectedKeywordsStatsGq;
  dataRef?: ProjectKeywordGq[];
  tableUIState?: TableUIState;
  page?: KeywordTablePage;
}

export const KeywordTablePagination: React.FC<KeywordTablePaginationProps> = ({
  tableRef,
  currentPage,
  totalCount,
  pagingState,
  setPagingState,
  selectedKeywordStats,
  dataRef,
  page,
}) => {

  const topUIHeight = (tableRef?.offsetTop ?? 0) + 54 + 40; // Navbar is 56px, toolbar is 40px, tablehead is 54px, footer is 40 pixels, 
  const totalPages = Math.ceil(totalCount / (pagingState.amountPerPage ?? 10));
  const [tableWidth, setTableWidth] = useState(tableRef?.clientWidth ?? 800);

  const [fixedPagingFooter, setFixedPagingFooter] = useState(
    tableRef?.clientHeight > window.innerHeight - topUIHeight
  );
  const updateSizes = () => {
    setTableWidth(tableRef?.clientWidth ?? 800);
    setFixedPagingFooter(tableRef?.clientHeight > window.innerHeight - topUIHeight);
  };

  // We need to update the sizes prior to the DOM-paint in case the table got smaller/larger based on the data sent to the table
  useLayoutEffect(() => {
    updateSizes();
  }, [tableRef, selectedKeywordStats, dataRef, topUIHeight]);

  // We need to update the sizes in case the window size has changed
  useEffect(() => {
    window.addEventListener('resize', updateSizes);
    return () => window.removeEventListener('resize', updateSizes);
  }, [tableRef, selectedKeywordStats, dataRef, topUIHeight]);

  return (
    <div
      className={`
      ${fixedPagingFooter && 'fixed bottom-0 left-0 px-0 w-full'}
      border-t border-gray-100 bg-gray-50 font-sans font-semibold
  `}
    >
      <div
        className="flex flex-row items-center gap-8 h-10 px-4"
        style={{
          width: fixedPagingFooter
            ? Math.min(tableWidth, window.innerWidth - tableRef?.offsetLeft)
            : undefined,
          marginLeft: fixedPagingFooter ? tableRef?.offsetLeft : undefined,
        }}
      >
        {/* Paging */}
        <div className="flex flex-row items-center gap-1">
          <VoxelText variant="table" noWrap>
            Page {currentPage + 1} / {totalPages}
          </VoxelText>
          <div className="w-2" />
          <VoxelIconButton
            size="lg"
            icon={<FontAwesomeIcon icon={faChevronLeft} />}
            color="white"
            disabled={pagingState.page <= 0}
            onClick={() =>
              setPagingState((prev) => ({
                ...prev,
                page: Math.max(prev.page - 1, 0),
              }))
            }
          />
          <VoxelIconButton
            size="lg"
            icon={<FontAwesomeIcon icon={faChevronRight} />}
            color="white"
            disabled={pagingState.page >= totalPages - 1}
            onClick={() =>
              setPagingState((prev) => ({
                ...prev,
                page: Math.min(prev.page + 1, totalPages - 1),
              }))
            }
          />
        </div>
        {/* Amount per page*/}
        <VoxelSelect
          inputClassNames="!bg-voxel-gray-50"
          outlined={false}
          size="sm"
          label="Keywords per page"
          options={[
            { title: '10', value: 10 },
            { title: '15', value: 15 },
            { title: '25', value: 25 },
            { title: '50', value: 50 },
            { title: '100', value: 100 },
          ]}
          selectedOptionValue={pagingState.amountPerPage}
          inputWidthClass="w-24"
          setSelectedOption={(option) =>
            setPagingState((prev) => ({
              ...prev,
              page: 0,
              amountPerPage: option.value,
            }))
          }
        />
        <div className="flex-grow" />
        {selectedKeywordStats && (
          <div className="flex flex-row flex-nowrap items-center gap-4 cursor-default">
            <VoxelTooltipShort message={`Number of selected keywords`}>
              <VoxelText variant="table" color="slight" noWrap>
                Keywords&nbsp;&nbsp;
                <span className="text-gray-700">
                  {formatNumber(selectedKeywordStats?.selectedCount, 0)}
                </span>
              </VoxelText>
            </VoxelTooltipShort>
            <div className="h-7 bg-voxel-gray-200 w-[1px]" />
            <VoxelTooltipShort message={`Search volume for selected keywords`}>
              <VoxelText variant="table" color="slight" noWrap>
                Volume&nbsp;&nbsp;
                <span className="text-gray-700">
                  {formatNumber(selectedKeywordStats?.selectedVolume, 0)}
                </span>
              </VoxelText>
            </VoxelTooltipShort>
            <div className="h-7 bg-voxel-gray-200 w-[1px]" />
            <VoxelTooltipShort message={`Tagged keywords`}>
              <VoxelText variant="table" color="slight" noWrap>
                <FontAwesomeIcon icon={faTag} className="text-gray-300 mr-2" />
                <span className="text-gray-700 mr-1">
                  {formatPercent(
                    !selectedKeywordStats?.selectedCount
                      ? 0
                      : selectedKeywordStats?.selectedTaggedCount /
                      selectedKeywordStats?.selectedCount,
                    0
                  )}
                  %
                </span>
              </VoxelText>
            </VoxelTooltipShort>
            <div className="h-7 bg-voxel-gray-200 w-[1px]" />
            <VoxelTooltipShort message={`Rank enabled keywords`}>
              <VoxelText variant="table" color="slight" noWrap>
                <FontAwesomeIcon icon={faTrophy} className="text-gray-300 mr-2" />
                <span className="text-gray-700 mr-1">
                  {formatPercent(
                    !selectedKeywordStats?.selectedCount
                      ? 0
                      : selectedKeywordStats?.selectedUpdateRanksCount /
                      selectedKeywordStats?.selectedCount,
                    0
                  )}
                  %
                </span>
              </VoxelText>
            </VoxelTooltipShort>
            <div className="h-7 bg-voxel-gray-200 w-[1px]" />
          </div>
        )}
      </div>
    </div>
  );
};

export default KeywordTablePagination;
